import React, { createContext, useContext, useState, useEffect } from 'react';
import { authAPI, usersAPI, settingsAPI } from '../services/api';

const AuthContext = createContext(null);

export const useAuth = () => useContext(AuthContext);

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Check for existing session
    const savedUser = authAPI.getCurrentUser();
    if (savedUser && authAPI.isAuthenticated()) {
      setUser(savedUser);
    }
    setLoading(false);
  }, []);

  const login = async (username, password) => {
    try {
      const result = await authAPI.login(username, password);
      if (result.success) {
        setUser(result.user);
        
        // Store center code for logout redirect
        if (result.user.role === 'admin' && result.user.invitation_code) {
          localStorage.setItem('tcf_center_code', result.user.invitation_code);
        } else if (result.user.center_id) {
          // Fetch admin's invitation code for regular users
          try {
            const adminData = await usersAPI.getById(result.user.center_id);
            if (adminData?.invitation_code) {
              localStorage.setItem('tcf_center_code', adminData.invitation_code);
            }
          } catch (e) {
            console.error('Could not fetch admin code:', e);
          }
        } else {
          // SuperAdmin or user without center - clear the code
          localStorage.removeItem('tcf_center_code');
        }
        
        return { success: true };
      }
      return { success: false, message: result.message || 'Identifiants invalides' };
    } catch (error) {
      console.error('Login error:', error);
      return { success: false, message: 'Erreur de connexion au serveur' };
    }
  };

  const register = async (username, password, additionalInfo = {}) => {
    try {
      const result = await authAPI.register({
        username,
        password,
        email: additionalInfo.email || username,
        nom: additionalInfo.nom || '',
        telephone: additionalInfo.telephone || '',
        pays: additionalInfo.pays || '',
        invitation_code: additionalInfo.invitationCode || null
      });
      return result;
    } catch (error) {
      console.error('Register error:', error);
      return { success: false, message: 'Erreur de connexion au serveur' };
    }
  };

  const loginWithGoogle = async (googleUser) => {
    try {
      // This is called from AuthCallback after getting session data
      const result = await authAPI.googleCallback(googleUser.session_id);
      if (result.success) {
        setUser(result.user);
        return { success: true, isNewUser: !result.user.created_at };
      }
      return { success: false, message: result.message };
    } catch (error) {
      console.error('Google login error:', error);
      return { success: false, message: 'Erreur de connexion Google' };
    }
  };

  const logout = async () => {
    const userId = user?.id;
    await authAPI.logout(userId);
    setUser(null);
  };

  const updateUserCredits = async (userId, newCredits) => {
    try {
      await usersAPI.update(userId, { credits_ia: newCredits });
      if (user && user.id === userId) {
        const updatedUser = { ...user, credits_ia: newCredits };
        setUser(updatedUser);
        localStorage.setItem('tcf-current-user', JSON.stringify(updatedUser));
      }
      return true;
    } catch (error) {
      console.error('Update credits error:', error);
      return false;
    }
  };

  const updateUserInfo = async (userId, updates) => {
    try {
      const result = await usersAPI.update(userId, updates);
      if (result.success && user && user.id === userId) {
        const updatedUser = { ...user, ...updates };
        setUser(updatedUser);
        localStorage.setItem('tcf-current-user', JSON.stringify(updatedUser));
      }
      return result.success;
    } catch (error) {
      console.error('Update user error:', error);
      return false;
    }
  };

  const refreshUser = async () => {
    try {
      const result = await authAPI.getMe();
      if (result.success) {
        setUser(result.user);
        localStorage.setItem('tcf-current-user', JSON.stringify(result.user));
      }
    } catch (error) {
      console.error('Refresh user error:', error);
    }
  };

  return (
    <AuthContext.Provider value={{ 
      user, 
      loading, 
      login, 
      register, 
      loginWithGoogle, 
      logout, 
      updateUserCredits, 
      updateUserInfo,
      refreshUser
    }}>
      {children}
    </AuthContext.Provider>
  );
};
